/**
 * Bookmark Service
 * Handles API communication for spatial bookmarks CRUD operations
 */

import type { Bookmark, CreateBookmarkRequest, UpdateBookmarkRequest } from '../types/bookmark';
import { getSessionToken } from '../utils/sessionManager';
import { API_ORIGIN } from '../config/api';

// Empty string in production → same-origin /api proxy
const API_URL = API_ORIGIN;

function getAuthHeaders(): HeadersInit {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const csrfMatch = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
  if (csrfMatch && csrfMatch[2]) {
    headers['X-CSRF-Token'] = csrfMatch[2];
  }

  return headers;
}

/**
 * Fetch all bookmarks for the authenticated user
 */
export async function fetchBookmarks(): Promise<Bookmark[]> {
  const response = await fetch(`${API_URL}/api/bookmarks`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to fetch bookmarks');
  }

  return data.data;
}

/**
 * Create a new bookmark
 */
export async function createBookmark(request: CreateBookmarkRequest): Promise<Bookmark> {
  const response = await fetch(`${API_URL}/api/bookmarks`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to create bookmark');
  }

  return data.data;
}

/**
 * Update a bookmark's name and description
 */
export async function updateBookmark(
  bookmarkId: string,
  request: UpdateBookmarkRequest
): Promise<Bookmark> {
  const response = await fetch(`${API_URL}/api/bookmarks/${bookmarkId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to update bookmark');
  }

  return data.data;
}

/**
 * Delete a bookmark
 */
export async function deleteBookmark(bookmarkId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/bookmarks/${bookmarkId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Failed to delete bookmark');
  }
}
